import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const COUNTRIES = [
  { code: "GLOBAL", name: "Global" }, { code: "US", name: "USA" },
  { code: "FR", name: "France" }, { code: "GB", name: "UK" },
  { code: "IN", name: "India" }, { code: "BR", name: "Brazil" },
  { code: "JP", name: "Japan" }, { code: "DE", name: "Germany" },
];

const searchUrl = (platform, term) => {
  const q = encodeURIComponent(term.replace(/^#/, ""));
  if (platform === "tiktok") return `https://www.tiktok.com/search?q=${q}`;
  return `https://www.instagram.com/explore/tags/${q}/`;
};

/** LLM-curated radar for TikTok / Instagram (Option A — free tier).
 * Not real view counts — editorial signal from Universtrend search data.
 */
export default function ViralRadarPanel({ platform }) {
  const [country, setCountry] = useState("GLOBAL");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    try {
      const { data: res } = await api.get(`/viral/${platform}`, {
        params: { country, count: 10, force },
      });
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Radar unavailable");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(false); /* eslint-disable-next-line */ }, [platform, country]);

  const platformLabel = platform === "tiktok" ? "TikTok" : "Instagram Reels";
  const platformColor = platform === "tiktok" ? "#000000" : "#E4405F";

  return (
    <section data-testid={`viral-panel-${platform}`} className="border-b border-black">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-8">
        {/* Info banner — honesty about what this is */}
        <div className="mb-6 border border-black bg-[#fffbea] px-4 py-3 flex flex-wrap items-start gap-3">
          <span className="w-2 h-2 mt-1.5 bg-[#ff3b30] flex-shrink-0" />
          <div className="flex-1">
            <div className="overline text-[#52525b] mb-1">Editorial Radar · Free tier</div>
            <p className="text-sm text-[#3f3f46] leading-snug">
              {platformLabel} has no public trending-videos API — this radar is an editorial signal
              curated by AI from today&apos;s top web-search trends. Real view counts + live video links
              require the <b>Premium data tier</b> (coming soon — join the waitlist below).
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <span className="overline text-[#52525b]">Country</span>
          <select
            data-testid={`viral-country-${platform}`}
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="border border-black bg-white px-3 py-2 font-mono text-xs uppercase tracking-widest"
          >
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
          <button
            data-testid={`viral-refresh-${platform}`}
            onClick={() => load(true)}
            disabled={refreshing || loading}
            className="border border-black bg-white px-3 py-2 overline hover:bg-black hover:text-white disabled:opacity-50"
          >
            {refreshing ? "Regenerating…" : "Refresh radar"}
          </button>
          {data?.cached_at && (
            <span className="ml-auto font-mono text-xs text-[#52525b]">
              {data.fresh ? "Fresh" : "Cached"} · {new Date(data.cached_at).toLocaleString()}
            </span>
          )}
        </div>

        {loading ? (
          <div className="py-20 text-center font-mono text-xs uppercase tracking-widest text-[#52525b]">
            Scanning {platformLabel} culture…
          </div>
        ) : !data?.items?.length ? (
          <div className="py-20 text-center font-mono text-xs uppercase tracking-widest text-[#52525b]">
            No radar signal yet. Try Refresh.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-l border-t border-black">
            {data.items.map((it) => (
              <article
                key={it.id}
                data-testid={`viral-card-${it.id}`}
                className="border-r border-b border-black p-5 hover:bg-[#fafafa] transition-colors"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span
                    className="overline"
                    style={{ color: platformColor }}
                  >
                    {platformLabel}
                  </span>
                  <span className="font-mono text-xs">
                    Conf <b className="text-[#ff3b30]">{it.confidence}</b>
                  </span>
                </div>

                <div className="mt-2 overline text-[#52525b]">Trend · {it.trend_term}</div>

                <div className="mt-3 font-display font-extrabold text-xl tracking-tighter leading-tight">
                  &ldquo;{it.hook}&rdquo;
                </div>

                <p className="mt-3 text-sm text-[#3f3f46] leading-relaxed">
                  {it.concept}
                </p>

                <div className="mt-4 font-mono text-[11px] uppercase tracking-widest text-[#ff3b30]">
                  Why it works
                </div>
                <p className="mt-1 text-sm italic text-[#52525b]">{it.why_it_works}</p>

                <div className="mt-4 font-mono text-[11px] uppercase tracking-widest text-[#52525b]">
                  {platform === "tiktok" ? "Sound / style" : "Visual style"}
                </div>
                <p className="mt-1 text-sm">{it.sound_or_style}</p>

                <div className="mt-4 flex justify-between items-center font-mono text-xs">
                  <span className="text-[#52525b]">Est. reach</span>
                  <span className="font-bold">{it.typical_view_range}</span>
                </div>

                <div className="mt-3 flex flex-wrap gap-1">
                  {it.hashtags.map((h, i) => (
                    <span key={i} className="font-mono text-[10px] px-1.5 py-0.5 border border-black">
                      {h}
                    </span>
                  ))}
                </div>

                <div className="mt-4 flex gap-2">
                  <a
                    href={searchUrl(platform, it.hashtags?.[0] || it.trend_term)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`viral-search-${it.id}`}
                    className="flex-1 text-center overline border border-black bg-black text-white px-3 py-2 hover:bg-[#ff3b30]"
                  >
                    Search on {platform === "tiktok" ? "TikTok" : "Instagram"} →
                  </a>
                  <button
                    onClick={() => {
                      const payload = `${it.hook}\n\n${it.concept}\n\nHashtags: ${it.hashtags.join(" ")}`;
                      navigator.clipboard.writeText(payload).then(
                        () => toast.success("Concept copied"),
                        () => toast.error("Clipboard blocked")
                      );
                    }}
                    data-testid={`viral-copy-${it.id}`}
                    className="overline border border-black px-3 py-2 hover:bg-black hover:text-white"
                  >
                    Copy
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
